<template>
  <section class="sticky top-16 z-30 border-b border-border/50 bg-white">
    <div class="mx-auto max-w-6xl px-4 md:px-6">
      <div class="flex flex-wrap items-center gap-2 py-3">

        <!-- ── Service type ── -->
        <div ref="serviceRef" class="relative">
          <button
            type="button"
            :class="[
              'inline-flex h-9 items-center gap-2 rounded-full border px-4 text-sm font-semibold transition-colors',
              modelServiceType
                ? (activeBrand[modelServiceType] ?? 'bg-foreground text-background border-transparent')
                : 'border-border/60 bg-white text-foreground hover:bg-muted/60',
            ]"
            @click="togglePanel('service')"
          >
            <Icon :name="activeServiceIcon" size="14" class="opacity-80" />
            {{ activeServiceLabel }}
            <Icon name="tabler:chevron-down" size="12" :class="['opacity-50 transition-transform', openPanel === 'service' && 'rotate-180']" />
          </button>
          <FilterPanel :open="openPanel === 'service'" class="w-52">
            <p class="mb-2 px-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Service type</p>
            <button
              v-for="opt in serviceTypeOptions"
              :key="opt.value"
              type="button"
              :class="[
                'flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors',
                (modelServiceType ?? '__all__') === opt.value
                  ? 'bg-foreground/8 text-foreground'
                  : 'text-foreground hover:bg-muted/60',
              ]"
              @click="onServiceTypeChange(opt.value)"
            >
              <Icon :name="opt.icon" size="18" :class="['shrink-0', serviceIconColor[opt.value] ?? 'text-muted-foreground']" />
              {{ opt.label }}
              <Icon v-if="(modelServiceType ?? '__all__') === opt.value" name="tabler:check" size="14" class="ml-auto text-foreground/60" />
            </button>
          </FilterPanel>
        </div>

        <!-- ── Sort ── -->
        <div ref="sortRef" class="relative">
          <button
            type="button"
            class="inline-flex h-9 items-center gap-2 rounded-full border border-border/60 bg-white px-4 text-sm font-semibold text-foreground transition-colors hover:bg-muted/60"
            @click="togglePanel('sort')"
          >
            <Icon name="tabler:arrows-sort" size="14" class="text-muted-foreground" />
            {{ activeSortLabel }}
            <Icon name="tabler:chevron-down" size="12" :class="['text-muted-foreground/60 transition-transform', openPanel === 'sort' && 'rotate-180']" />
          </button>
          <FilterPanel :open="openPanel === 'sort'" class="w-52">
            <p class="mb-2 px-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Sort by</p>
            <button
              v-for="opt in sortOptions"
              :key="opt.value"
              type="button"
              :class="[
                'flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors',
                modelSort === opt.value ? 'bg-foreground/8 text-foreground' : 'text-foreground hover:bg-muted/60',
              ]"
              @click="() => { emit('update:modelSort', opt.value); closePanel(); }"
            >
              <Icon :name="opt.icon" size="18" class="shrink-0 text-muted-foreground" />
              {{ opt.label }}
              <Icon v-if="modelSort === opt.value" name="tabler:check" size="14" class="ml-auto text-foreground/60" />
            </button>
          </FilterPanel>
        </div>

        <!-- ── Radius ── -->
        <div ref="radiusRef" class="relative">
          <button
            type="button"
            class="inline-flex h-9 items-center gap-2 rounded-full border border-border/60 bg-white px-4 text-sm font-semibold text-foreground transition-colors hover:bg-muted/60"
            @click="togglePanel('radius')"
          >
            <Icon name="tabler:circle-dashed" size="14" class="text-muted-foreground" />
            {{ modelRadius }} km
            <Icon name="tabler:chevron-down" size="12" :class="['text-muted-foreground/60 transition-transform', openPanel === 'radius' && 'rotate-180']" />
          </button>
          <FilterPanel :open="openPanel === 'radius'" class="w-44">
            <p class="mb-2 px-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Search radius</p>
            <button
              v-for="r in RADIUS_OPTIONS"
              :key="r"
              type="button"
              :class="[
                'flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors',
                modelRadius === r ? 'bg-foreground/8 text-foreground' : 'text-foreground hover:bg-muted/60',
              ]"
              @click="() => { emit('update:modelRadius', r); closePanel(); }"
            >
              <Icon name="tabler:map-pin" size="18" class="shrink-0 text-muted-foreground" />
              {{ r }} km
              <Icon v-if="modelRadius === r" name="tabler:check" size="14" class="ml-auto text-foreground/60" />
            </button>
          </FilterPanel>
        </div>

        <!-- ── Rating ── -->
        <div ref="ratingRef" class="relative">
          <button
            type="button"
            class="inline-flex h-9 items-center gap-2 rounded-full border border-border/60 bg-white px-4 text-sm font-semibold text-foreground transition-colors hover:bg-muted/60"
            @click="togglePanel('rating')"
          >
            <Icon name="tabler:star-filled" size="14" class="text-amber-400" />
            {{ activeRatingLabel }}
            <Icon name="tabler:chevron-down" size="12" :class="['text-muted-foreground/60 transition-transform', openPanel === 'rating' && 'rotate-180']" />
          </button>
          <FilterPanel :open="openPanel === 'rating'" class="w-48">
            <p class="mb-2 px-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Minimum rating</p>
            <button
              type="button"
              :class="[
                'flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors',
                modelMinRating == null ? 'bg-foreground/8 text-foreground' : 'text-foreground hover:bg-muted/60',
              ]"
              @click="() => { emit('update:modelMinRating', undefined); closePanel(); }"
            >
              <Icon name="tabler:star" size="18" class="shrink-0 text-muted-foreground" />
              Any Rating
              <Icon v-if="modelMinRating == null" name="tabler:check" size="14" class="ml-auto text-foreground/60" />
            </button>
            <button
              v-for="r in RATING_OPTIONS"
              :key="r.value"
              type="button"
              :class="[
                'flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors',
                modelMinRating === r.value ? 'bg-foreground/8 text-foreground' : 'text-foreground hover:bg-muted/60',
              ]"
              @click="() => { emit('update:modelMinRating', r.value); closePanel(); }"
            >
              <span class="flex shrink-0 items-center gap-0.5">
                <Icon
                  v-for="n in r.stars"
                  :key="n"
                  name="tabler:star-filled"
                  size="14"
                  class="text-amber-400"
                />
              </span>
              {{ r.label }}
              <Icon v-if="modelMinRating === r.value" name="tabler:check" size="14" class="ml-auto text-foreground/60" />
            </button>
          </FilterPanel>
        </div>

        <!-- ── Price range ── -->
        <div ref="priceRef" class="relative">
          <button
            type="button"
            :class="[
              'inline-flex h-9 items-center gap-2 rounded-full border border-border/60 px-4 text-sm font-semibold transition-colors',
              openPanel === 'price' || hasPriceFilter ? 'bg-muted text-foreground' : 'bg-white text-foreground hover:bg-muted/60',
            ]"
            @click="togglePanel('price')"
          >
            <Icon name="tabler:currency-euro" size="14" class="text-muted-foreground" />
            {{ priceLabel }}
            <Icon name="tabler:chevron-down" size="12" :class="['text-muted-foreground/60 transition-transform', openPanel === 'price' && 'rotate-180']" />
          </button>
          <FilterPanel :open="openPanel === 'price'" class="w-72">
            <p class="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Price range</p>
            <div class="flex items-center gap-2">
              <div class="relative flex-1">
                <span class="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">€</span>
                <input
                  v-model="minPriceInput"
                  type="number" min="0" max="99999" step="1" placeholder="Min"
                  class="h-10 w-full rounded-full border border-border bg-white pl-7 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-foreground/20"
                />
              </div>
              <span class="text-sm text-muted-foreground">–</span>
              <div class="relative flex-1">
                <span class="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">€</span>
                <input
                  v-model="maxPriceInput"
                  type="number" min="0" max="99999" step="1" placeholder="Max"
                  class="h-10 w-full rounded-full border border-border bg-white pl-7 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-foreground/20"
                />
              </div>
            </div>
            <div class="mt-3 flex items-center justify-between gap-2">
              <button type="button" class="text-sm font-medium text-muted-foreground hover:text-foreground" @click="clearPrice">Clear</button>
              <button type="button" class="inline-flex h-9 items-center rounded-full bg-foreground px-5 text-sm font-semibold text-background hover:bg-foreground/90" @click="applyPrice">Apply</button>
            </div>
          </FilterPanel>
        </div>

        <!-- ── Near me ── -->
        <button
          type="button"
          :class="[
            'inline-flex h-9 items-center gap-2 rounded-full border px-4 text-sm font-semibold transition-colors',
            hasLocation || isLocating
              ? 'border-transparent bg-foreground/10 text-foreground'
              : 'border-border/60 bg-white text-foreground hover:bg-muted/60',
          ]"
          :disabled="isLocating"
          @click="emit('requestLocation')"
        >
          <Icon v-if="isLocating" name="tabler:loader-2" size="14" class="animate-spin" />
          <Icon v-else name="tabler:current-location" size="14" />
          {{ hasLocation ? 'My location' : 'Near me' }}
        </button>

        <div class="flex-1" />

        <!-- Result count -->
        <span class="shrink-0 text-sm text-muted-foreground">
          {{ resultCount }} {{ resultCount === 1 ? 'result' : 'results' }}
        </span>

        <!-- View toggle -->
        <div class="flex rounded-full border border-border/60 bg-white p-1">
          <button
            type="button"
            :class="[
              'inline-flex h-7 items-center gap-2 rounded-full px-4 text-sm font-semibold transition-colors',
              view === 'list' ? 'bg-foreground text-background shadow-sm' : 'text-muted-foreground hover:text-foreground',
            ]"
            @click="emit('update:view', 'list')"
          >
            <Icon name="tabler:list" size="15" />
            List
          </button>
          <button
            type="button"
            :class="[
              'inline-flex h-7 items-center gap-2 rounded-full px-4 text-sm font-semibold transition-colors',
              view === 'map' ? 'bg-foreground text-background shadow-sm' : 'text-muted-foreground hover:text-foreground',
            ]"
            @click="emit('update:view', 'map')"
          >
            <Icon name="tabler:map" size="15" />
            Map
          </button>
        </div>

      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch, defineComponent, h, Transition } from 'vue';
import { useI18n } from '#imports';
import { RADIUS_OPTIONS } from '#bubba/shared/utils/geo';
import { SERVICE_TYPE_CONFIG } from '#bubba/shared/utils/serviceTypes';

// Inline panel component — matches price popover style exactly
const FilterPanel = defineComponent({
  props: { open: Boolean },
  setup(props, { slots }) {
    return () => h(Transition, {
      enterActiveClass: 'transition ease-out duration-150',
      enterFromClass: 'opacity-0 -translate-y-1',
      enterToClass: 'opacity-100 translate-y-0',
      leaveActiveClass: 'transition ease-in duration-100',
      leaveFromClass: 'opacity-100 translate-y-0',
      leaveToClass: 'opacity-0 -translate-y-1',
    }, () => props.open
      ? h('div', {
          class: 'absolute left-0 top-full z-40 mt-2 rounded-2xl border border-border bg-white p-4 shadow-xl',
        }, slots.default?.())
      : null,
    );
  },
});

const { $ts } = useI18n();

const RATING_OPTIONS = [
  { value: 4.0, label: '4.0+', stars: 4 },
  { value: 4.5, label: '4.5+', stars: 4 },
  { value: 4.8, label: '4.8+', stars: 5 },
];

// Static brand-color lookup — Tailwind JIT requires literal class strings (no interpolation).
const activeBrand: Record<string, string> = {
  grooming:   'bg-groom text-white border-transparent',
  vet:        'bg-vet text-white border-transparent',
  walking:    'bg-emerald-500 text-white border-transparent',
  daycare:    'bg-amber-400 text-foreground border-transparent',
  boarding:   'bg-blue-500 text-white border-transparent',
  petsitting: 'bg-rose-400 text-white border-transparent',
  training:   'bg-purple-500 text-white border-transparent',
  transport:  'bg-slate-500 text-white border-transparent',
  online_vet: 'bg-cyan-500 text-white border-transparent',
};

// Tailwind JIT safe — same constraint as activeBrand.
const serviceIconColor: Record<string, string> = {
  __all__:    'text-muted-foreground',
  grooming:   'text-orange-500',
  vet:        'text-teal-600',
  walking:    'text-emerald-600',
  daycare:    'text-amber-500',
  boarding:   'text-blue-500',
  petsitting: 'text-rose-500',
  training:   'text-purple-500',
  transport:  'text-slate-500',
  online_vet: 'text-cyan-600',
};

// Built from shared SERVICE_TYPE_CONFIG so icons stay in sync with the rest of the app.
const SERVICE_TYPE_KEYS = ['grooming', 'vet', 'walking', 'daycare', 'boarding', 'petsitting', 'training', 'transport', 'online_vet'] as const;

const serviceTypeOptions = computed(() => [
  { value: '__all__', label: $ts('servicesSearch.filters.allTypes'), icon: 'tabler:layout-grid' },
  ...SERVICE_TYPE_KEYS.map(key => ({
    value: key,
    label: $ts(`servicesSearch.serviceTypes.${key}`),
    icon: SERVICE_TYPE_CONFIG[key]?.icon ?? 'tabler:paw',
  })),
]);

const sortOptions = [
  { value: 'popular',    label: 'Most Popular',       icon: 'tabler:flame'         },
  { value: 'price_asc',  label: 'Price: Low to High', icon: 'tabler:sort-ascending' },
  { value: 'price_desc', label: 'Price: High to Low', icon: 'tabler:sort-descending'},
  { value: 'rating',     label: 'Highest Rated',      icon: 'tabler:star-filled'   },
  { value: 'distance',   label: 'Nearest',            icon: 'tabler:map-pin'       },
  { value: 'newest',     label: 'Newest',             icon: 'tabler:clock'         },
];

const props = defineProps<{
  modelServiceType: string | undefined;
  modelCity: string | undefined;
  modelSort: string;
  modelRadius: number;
  modelMinRating: number | undefined;
  modelMinPrice: number | undefined;
  modelMaxPrice: number | undefined;
  cities: string[];
  resultCount: number;
  isLocating: boolean;
  hasLocation: boolean;
  view: 'list' | 'map';
}>();

const emit = defineEmits<{
  'update:modelServiceType': [value: string | undefined];
  'update:modelCity':        [value: string | undefined];
  'update:modelSort':        [value: string];
  'update:modelRadius':      [value: number];
  'update:modelMinRating':   [value: number | undefined];
  'update:modelMinPrice':    [value: number | undefined];
  'update:modelMaxPrice':    [value: number | undefined];
  'update:view':             [value: 'list' | 'map'];
  'requestLocation':         [];
}>();

// Panel state — only one open at a time
type PanelName = 'service' | 'sort' | 'radius' | 'rating' | 'price';
const openPanel = ref<PanelName | null>(null);

const serviceRef = ref<HTMLElement | null>(null);
const sortRef    = ref<HTMLElement | null>(null);
const radiusRef  = ref<HTMLElement | null>(null);
const ratingRef  = ref<HTMLElement | null>(null);
const priceRef   = ref<HTMLElement | null>(null);

const panelRefs: Record<PanelName, typeof serviceRef> = {
  service: serviceRef,
  sort: sortRef,
  radius: radiusRef,
  rating: ratingRef,
  price: priceRef,
};

function togglePanel(name: PanelName) {
  openPanel.value = openPanel.value === name ? null : name;
}

function closePanel() {
  openPanel.value = null;
}

function onDocumentClick(e: MouseEvent) {
  if (!openPanel.value) return;
  const ref = panelRefs[openPanel.value];
  if (ref.value?.contains(e.target as Node)) return;
  openPanel.value = null;
}

onMounted(() => document.addEventListener('click', onDocumentClick, true));
onBeforeUnmount(() => document.removeEventListener('click', onDocumentClick, true));

// Active labels
const activeServiceLabel = computed(() => {
  const opt = serviceTypeOptions.value.find(o => o.value === (props.modelServiceType ?? '__all__'));
  return opt?.label ?? $ts('servicesSearch.filters.allTypes');
});

const activeServiceIcon = computed(() => {
  const opt = serviceTypeOptions.value.find(o => o.value === (props.modelServiceType ?? '__all__'));
  return opt?.icon ?? 'tabler:layout-grid';
});

const activeSortLabel = computed(() => {
  const opt = sortOptions.find(o => o.value === props.modelSort);
  return opt?.label ?? 'Most Popular';
});

const activeRatingLabel = computed(() => {
  if (props.modelMinRating == null) return 'Any Rating';
  const opt = RATING_OPTIONS.find(r => r.value === props.modelMinRating);
  return opt?.label ?? 'Any Rating';
});

function onServiceTypeChange(value: string) {
  emit('update:modelServiceType', value === '__all__' ? undefined : value);
  closePanel();
}

// Price range
const minPriceInput = ref(props.modelMinPrice != null ? String(Math.round(props.modelMinPrice / 100)) : '');
const maxPriceInput = ref(props.modelMaxPrice != null ? String(Math.round(props.modelMaxPrice / 100)) : '');

watch(() => props.modelMinPrice, (v) => { minPriceInput.value = v != null ? String(Math.round(v / 100)) : ''; });
watch(() => props.modelMaxPrice, (v) => { maxPriceInput.value = v != null ? String(Math.round(v / 100)) : ''; });

const hasPriceFilter = computed(() => props.modelMinPrice != null || props.modelMaxPrice != null);

const priceLabel = computed(() => {
  const min = props.modelMinPrice != null ? Math.round(props.modelMinPrice / 100) : null;
  const max = props.modelMaxPrice != null ? Math.round(props.modelMaxPrice / 100) : null;
  if (min != null && max != null) return `€${min}–€${max}`;
  if (min != null) return `From €${min}`;
  if (max != null) return `Up to €${max}`;
  return 'Price';
});

function applyPrice() {
  emit('update:modelMinPrice', minPriceInput.value ? Math.round(Number(minPriceInput.value) * 100) : undefined);
  emit('update:modelMaxPrice', maxPriceInput.value ? Math.round(Number(maxPriceInput.value) * 100) : undefined);
  closePanel();
}

function clearPrice() {
  minPriceInput.value = '';
  maxPriceInput.value = '';
  emit('update:modelMinPrice', undefined);
  emit('update:modelMaxPrice', undefined);
  closePanel();
}
</script>
